import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t } from '../i18n.js';
import BeheerMeldingen from './BeheerMeldingen.jsx';
import BeheerSchermen from './BeheerSchermen.jsx';
import BeheerDetectie from './BeheerDetectie.jsx';
import BeheerKlanten from './BeheerKlanten.jsx';
import BeheerGebruikers from './BeheerGebruikers.jsx';
import NessusKoppeling from '../components/NessusKoppeling.jsx';
import UmbrellaKoppeling from '../components/UmbrellaKoppeling.jsx';
import DuoKoppeling from '../components/DuoKoppeling.jsx';
import Connectiviteit from '../components/Connectiviteit.jsx';
import BeheerBeheerders from './BeheerBeheerders.jsx';
import SecureEndpointKoppeling from '../components/SecureEndpointKoppeling.jsx';
import ThreatIntel from '../components/ThreatIntel.jsx';

/**
 * Beheer, zoals in het prototype: per beheerder (een medewerker van ADTS) de rollen, klanten en of hij aan staat,
 * de gebruikers (wie via het klantportaal binnenkomt) en per rol de rechten. Een vinkje is meteen een wijziging en staat in het logboek. De server weigert wat de
 * laatste beheerder zou weghalen; die melding komt hier bovenaan.
 */
/* De onderdelen van Beheer; het actieve staat in de URL (?onderdeel=…), zodat een refresh op dezelfde plek blijft. */
const ONDERDELEN = new Set(['medewerkers', 'gebruikers', 'rollen', 'klanten', 'meldingen', 'detectie', 'schermen', 'connectiviteit', 'nessus', 'umbrella', 'duo', 'secureendpoint', 'intel']);

export default function Beheer({ mij }) {
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const gevraagd = params.get('onderdeel');
  const tab = ONDERDELEN.has(gevraagd) ? gevraagd : 'medewerkers';
  const zetTab = (id) => setParams(id === 'medewerkers' ? {} : { onderdeel: id }, { replace: true });
  const [rolTab, zetRolTab] = useState('beheerders');
  const [melding, zetMelding] = useState(null);
  const q = useQuery({ queryKey: ['beheer'], queryFn: api.beheer });

  const doe = async (actie) => {
    zetMelding(null);
    try {
      await actie();
    } catch (err) {
      zetMelding(t(err.message));
    }
    await client.invalidateQueries({ queryKey: ['beheer'] });
    /* Mijn eigen rechten kunnen zijn veranderd. */
    await client.invalidateQueries({ queryKey: ['ik'] });
  };

  if (q.isPending) return <p className="muted">{t('Laden…')}</p>;
  if (q.isError) return <div className="melder rood">{t(q.error.message)}</div>;
  const { employees, roles, tenants, permissions, customerUsers } = q.data.data;
  const medewerkerRollen = roles.filter((r) => !r.forCustomer);


  return (
    <>
      {/* Een zijmenu in groepen in plaats van tien knoppen op een rij. */}
      <div className="beheerlayout">
      <nav className="beheermenu" aria-label={t('Beheer')}>
        {[
          [t('Toegang'), [['medewerkers', t('Beheerders')], ['gebruikers', t('Gebruikers')], ['rollen', t('Rollen en rechten')]]],
          [t('Klanten'), [['klanten', t('Klanten')]]],
          [t('Meldingen en detectie'), [['meldingen', t('Meldingen')], ['detectie', t('Detectie')], ['schermen', t('SOC-schermen')]]],
          [t('Systeem'), [['connectiviteit', t('Connectiviteit')]]],
          [t('Koppelingen'), [['nessus', t('Nessus')], ['umbrella', 'Umbrella'], ['duo', 'Secure Connect'], ['secureendpoint', 'Secure Endpoint'], ['intel', t('Threat intel')]]],
        ].map(([kop, items]) => (
          <div key={kop} className="beheermenu-groep">
            <span className="label">{kop}</span>
            {items.map(([id, label]) => <button key={id} type="button" aria-current={tab === id ? 'page' : undefined} onClick={() => zetTab(id)}>{label}</button>)}
          </div>
        ))}
      </nav>
      <div className="beheerinhoud">
      {melding && <div className="melder rood" style={{ marginBottom: 14 }}>{melding}</div>}

      {tab === 'connectiviteit' ? <Connectiviteit /> : tab === 'intel' ? <ThreatIntel /> : tab === 'nessus' ? <NessusKoppeling /> : tab === 'umbrella' ? <UmbrellaKoppeling klanten={tenants} /> : tab === 'duo' ? <DuoKoppeling klanten={tenants} /> : tab === 'secureendpoint' ? <SecureEndpointKoppeling klanten={tenants} /> : tab === 'gebruikers' ? (
        <>
          <BeheerGebruikers gebruikers={customerUsers} rollen={roles.filter((r) => r.forCustomer)} doe={doe} />
          <p className="muted" style={{ fontSize: 13, marginTop: 10 }}>{t('Wat een rol voor klanten mag, staat onder Rollen en rechten › Klanten.')}</p>
        </>
      ) : tab === 'klanten' ? <BeheerKlanten klanten={tenants} doe={doe} /> : tab === 'schermen' ? <BeheerSchermen klanten={tenants} /> : tab === 'detectie' ? <BeheerDetectie /> : tab === 'meldingen' ? <BeheerMeldingen /> : tab === 'medewerkers' ? (
        <BeheerBeheerders employees={employees} rollen={medewerkerRollen} tenants={tenants} permissions={permissions} mij={mij} doe={doe} />
      ) : (
        <>
          {/* Twee soorten rollen, nooit door elkaar: die van medewerkers van ADTS en die van klanten. */}
          <div className="filters">
            <button type="button" className="chip" aria-pressed={rolTab === 'beheerders'} onClick={() => zetRolTab('beheerders')}>{t('Beheerders')}</button>
            <button type="button" className="chip" aria-pressed={rolTab === 'klanten'} onClick={() => zetRolTab('klanten')}>{t('Klanten')}</button>
          </div>
          <Rollen key={rolTab} data={q.data.data} doe={doe} voorKlanten={rolTab === 'klanten'} />
        </>
      )}
      </div>
      </div>
    </>
  );
}

/**
 * Rollen en rechten, zoals in ADTS-ONE: bovenin kies je de rol, daaronder staan zijn naam en zijn
 * rechten. Een rol erbij begint leeg en heet "Nieuwe rol"; je typt de naam eroverheen.
 */
function Rollen({ data, doe, voorKlanten = false }) {
  const { layout, customerPermissions } = data;
  /* De rollen van medewerkers of die van klanten, elk onder een eigen tab. */
  const roles = data.roles.filter((r) => r.forCustomer === voorKlanten);
  const [gekozen, zetGekozen] = useState(null);
  const [netNieuw, zetNetNieuw] = useState(null);
  const rol = roles.find((r) => r.code === gekozen) ?? roles[0];

  /* Meer dan één recht tegelijk: ze hangen samen. Het raster rekent uit wat er mee moet. */
  const zetRechten = (erbij, eraf) => doe(() => api.wijzigRol(rol.code, {
    permissions: [...new Set([...rol.permissions, ...erbij])].filter((p) => !eraf.includes(p)),
  }));

  const nieuw = () => doe(async () => {
    /* De code is een sleutel en geen naam: hij verandert nooit meer, dus hoeft hij niet mooi te zijn. */
    const codes = new Set(data.roles.map((r) => r.code));
    const voor = voorKlanten ? 'klantrol' : 'rol';
    let n = data.roles.length + 1;
    while (codes.has(`${voor}-${n}`)) n += 1;
    const code = `${voor}-${n}`;
    await api.maakRol({ code, label: t(voorKlanten ? 'Nieuwe rol voor klanten' : 'Nieuwe rol'), permissions: [], forCustomer: voorKlanten });
    zetGekozen(code); zetNetNieuw(code);
  });
  const knop = (r) => (
    <button key={r.code} type="button" aria-pressed={r.code === rol?.code} onClick={() => zetGekozen(r.code)}
      title={r.standard ? t('standaard') : undefined}>
      {t(r.label)} <span className="muted">({r.members})</span>
    </button>
  );

  return (
    <section className="card setblok">
      <div className="setkeuze" style={{ marginBottom: 12 }}>
        {roles.map(knop)}
        <button type="button" onClick={nieuw}>+ {t('Rol')}</button>
      </div>

      {rol && (
        <>
          <div className="setrij">
            <div className="setkop"><b>{t('Naam')}</b><span className="num">{rol.code}</span></div>
            <div className="rij">
              <input className="veld" defaultValue={t(rol.label)} key={rol.code} aria-label={t('Naam')}
                ref={(el) => { if (el && netNieuw === rol.code) { el.focus(); el.select(); zetNetNieuw(null); } }}
                onBlur={(e) => {
                  const naam = e.target.value.trim();
                  if (naam && naam !== t(rol.label)) doe(() => api.wijzigRol(rol.code, { label: naam }));
                }} />
              <span className="muted">
                {!rol.members ? t('nog niemand')
                  : rol.forCustomer ? t(rol.members === 1 ? 'Toegepast op {n} gebruiker' : 'Toegepast op {n} gebruikers', { n: rol.members })
                  : t(rol.members === 1 ? 'Toegepast op {n} medewerker' : 'Toegepast op {n} medewerkers', { n: rol.members })}
              </span>
              {/* Een standaardrol kwam met ZERO mee. De knop staat er dan niet in plaats van
                  uitgegrijsd: de zin zegt wat er wél mag. De server weigert het hoe dan ook. */}
              {rol.standard ? (
                <span className="muted" style={{ marginLeft: 'auto' }}>{t('Standaardrol — hernoemen en rechten aanpassen mag, verwijderen niet.')}</span>
              ) : (
                <button type="button" className="namelink gevaar" style={{ marginLeft: 'auto' }}
                  onClick={() => doe(async () => { await api.verwijderRol(rol.code); zetGekozen(null); })}>
                  {t('Rol verwijderen')}
                </button>
              )}
            </div>
          </div>

          <div className="setrij boven">
            <div className="setkop">
              <b>{t('Rechten')}</b>
              <span>{t('Wat hier niet staat, bestaat niet: een recht hoort bij code die het afdwingt.')}</span>
            </div>
            <Rechtenraster layout={layout} rol={rol} zet={zetRechten} toegestaan={rol.forCustomer ? customerPermissions : null} />
            {rol.forCustomer && <p className="noot" style={{ marginTop: 8 }}>
              {t('Een rol voor klanten kan alleen rechten hebben die voor klanten bestaan, en geldt alleen voor de eigen klant van een gebruiker. Ze openen nog niets: een onderdeel gaat pas voor klanten open als het daarvoor gebouwd en getest is.')}</p>}
          </div>
        </>
      )}

      {!voorKlanten && <p className="noot">
        {t('Rollen zijn optelbaar: iemand kan analist én engineer zijn, en dan tellen de rechten op. De code controleert altijd op een recht en nooit op de naam van een rol — een rol erbij is daarom geen release.')}
      </p>}
    </section>
  );
}

/**
 * De rechten als raster: één regel per onderdeel, met lezen, wijzigen en goedkeuren erachter.
 *
 * **Ze hangen samen**, zoals in ADTS-ONE: een vinkje verderop zet de lichtere vanzelf aan, een
 * vinkje weghalen haalt de zwaardere vanzelf weg. Goedkeuren zonder voorstellen bestaat niet.
 */
function Rechtenraster({ layout, rol, zet, toegestaan = null }) {
  /* Bij een rol voor klanten: alleen de rechten die voor klanten bestaan, de rest is er niet. */
  const kan = (recht) => !toegestaan || toegestaan.includes(recht);
  const KOP = { lezen: t('Lezen'), wijzigen: t('Wijzigen'), goedkeuren: t('Goedkeuren') };
  const { objecten, handelingen, overig } = layout;
  const heeft = (recht) => Boolean(recht) && rol.permissions.includes(recht);

  const wissel = (object, handeling, aan) => {
    const volgorde = handelingen.filter((h) => object.rechten[h]);
    const i = volgorde.indexOf(handeling);
    const raakt = aan ? volgorde.slice(0, i + 1) : volgorde.slice(i);
    const rechten = raakt.map((h) => object.rechten[h]);
    zet(aan ? rechten : [], aan ? [] : rechten);
  };

  return (
    <div>
      <table className="rechten">
        <colgroup><col /><col style={{ width: 96 }} /><col style={{ width: 96 }} /><col style={{ width: 110 }} /></colgroup>
        <thead>
          <tr><th>{t('Onderdeel')}</th>{handelingen.map((h) => <th key={h} className="m">{KOP[h]}</th>)}</tr>
        </thead>
        <tbody>
          {objecten.map((o) => (
            <tr key={o.id}>
              <td>{t(o.label)} <span className="num muted klein">{o.id}</span><br /><span className="muted klein">{t(o.uitleg)}</span></td>
              {handelingen.map((h) => (
                <td key={h} className="m">
                  {o.rechten[h] && kan(o.rechten[h])
                    ? <input type="checkbox" aria-label={`${t(o.label)} — ${KOP[h]}`} title={o.rechten[h]}
                        checked={heeft(o.rechten[h])} onChange={(e) => wissel(o, h, e.target.checked)} />
                    /* Een streepje zegt "hier valt niets te kiezen"; een leeg vakje zou "uit" zeggen. */
                    : <span className="muted">—</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {overig.length > 0 && (
        <>
          <p className="noot" style={{ margin: '14px 0 6px' }}>
            {t('Losse handelingen: die gaan niet over een onderdeel maar over iets wat je mag doen.')}
          </p>
          <table className="rechten">
            <colgroup><col /><col style={{ width: 110 }} /></colgroup>
            <thead><tr><th>{t('Handeling')}</th><th className="m">{t('Mag')}</th></tr></thead>
            <tbody>
              {overig.map((r) => (
                <tr key={r.id}>
                  <td>{t(r.label)}<br /><span className="muted klein">{t(r.uitleg)}</span></td>
                  <td className="m">
                    {kan(r.id) ? <input type="checkbox" aria-label={t(r.label)} checked={heeft(r.id)}
                      onChange={(e) => zet(e.target.checked ? [r.id] : [], e.target.checked ? [] : [r.id])} />
                      : <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
